import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import { publicOperationMiddleware } from '../../permissions/middlewares/publicOperationMiddleware.js';
import { checkPermissionMiddleware } from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { isNotNullish } from '@adornis/assert/isNullish.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { from, of, switchMap } from 'rxjs';
import { Booking } from '../../bookings/entities/Booking.js';
import { Group } from '../entities/Group.js';
import { findKostenstelleWithSuffix } from '../entities/Kostenstelle.js';
import { School } from '../entities/School.js';
import { resolveGetPublicSchoolInformation } from '../server/resolveGetPublicSchoolInformation.js';
import { resolveReleaseGroupBillingLock } from '../server/resolvers/resolveReleaseGroupBillingLock.js';
import { getAllSchools } from './getAllSchools.js';

const subscribeGroupsPaginatedFiltered = registerSubscription({
  type: () => [Group],
  operationName: 'subscribeGroupsPaginatedFiltered',
  middlewares: [authenticatedMiddleware],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) =>
    <Sel extends BaseQLSelectionSet<Group>>(gqlFields: Sel) =>
      from(publishFilter(Group._class)).pipe(
        switchMap(publishFilter =>
          watchEntireCollection(Group._collectionName + '-materialized').pipe(
            switchMap(async () => {
              const groupCol = await getRawCollection(Group._collectionName + '-materialized');
              const onlyInternal = filter.getFilterAspect<BooleanAspect>('onlyInternal')?.value;

              // handle search for kostenstellensuffix
              const indexOfSpecifier = filter.searchFields?.findIndex(field => field === 'kostenstelleSuffix');

              if (indexOfSpecifier !== undefined && indexOfSpecifier >= 0) {
                // remove it from the search fields as we cannot search for it normally
                filter.searchFields?.splice(indexOfSpecifier, 1);

                // the user could have entered the prefix(3) + suffix(2) so we need to detect it
                // if the entered number is below the string length of the combined (5 chars total)
                // we need to split it into prefix and suffix parts

                // instead add a regex for the custom field
                const searchValue = filter.search;

                if (isNotNullish(searchValue) && !isNaN(parseInt(searchValue))) {
                  filter.searchFields?.push('kostenstelleSuffixString');
                  // Check if the search value is 5 characters or less (prefix + suffix combined)
                  if (searchValue.length >= 4) {
                    // Split into prefix (first 3 chars) and suffix (last 2 chars)
                    const suffix = searchValue.slice(3);

                    // Add regex condition to $or array
                    filter.search = suffix;
                  }
                }
              }

              const selector = onlyInternal
                ? {
                    $or: [
                      { $and: [{ _class: School._class }, { isSgSchool: true }, filter.getMongoFilter()] },
                      { $and: [{ _class: { $ne: School._class } }, filter.getMongoFilter()] },
                    ],
                  }
                : filter.getMongoFilter();

              const pipeline = [
                {
                  $addFields: {
                    kostenstelleSuffixString: { $toString: '$kostenstelleSuffix' },
                  },
                },
                {
                  $match: {
                    $and: [publishFilter, selector],
                  },
                },

                { $sort: filter.getMongoOptions().sort },

                {
                  $skip: skip,
                },
                {
                  $limit: limit,
                },
                {
                  $project: gqlFields,
                },
              ];
              return groupCol.aggregate(pipeline).toArray() as Promise<Array<NarrowedEntity<Group, Sel>>>;
            }),
          ),
        ),
      ),
});

const subscribeSubGroupsPaginatedFiltered = registerSubscription({
  type: () => [Group],
  operationName: 'subscribeSubGroupsPaginatedFiltered',
  middlewares: [authenticatedMiddleware],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) =>
    <Sel extends BaseQLSelectionSet<Group>>(gqlFields: Sel) => {
      const groupID = filter.getFilterAspect<StringAspect>('groupID')?.value;

      return from(publishFilter(Group._class)).pipe(
        switchMap(publishFilter =>
          watchQuery<Sel>(
            Group._collectionName,
            {
              $and: [publishFilter, { parentIDs: groupID }, filter.getMongoFilter()],
            },
            {
              skip,
              limit,
              ...filter.getMongoOptions(),
              projection: gqlFields,
            },
          ),
        ),
      );
    },
});

const subscribeGroupsOfUser = registerSubscription({
  type: () => [Group],
  operationName: 'subscribeGroupsOfUser',
  middlewares: [authenticatedMiddleware],
  params: [{ type: () => String, name: 'personID' }],
  resolve:
    (personID: string) =>
    <Sel extends BaseQLSelectionSet<Group>>(gqlFields: Sel) =>
      from(publishFilter(Booking._class)).pipe(
        switchMap(publishFilter =>
          watchQuery(
            Booking._collectionName,
            {
              $and: publishFilter ? [publishFilter, { userID: personID }] : [{ userID: personID }],
            },
            {
              projection: { groupIDs: 1 },
            },
          ),
        ),

        switchMap(bookings => {
          const groupIDs = bookings.flatMap(booking => booking.groupIDs || []).filter(Boolean);
          if (!groupIDs.length) return of([]);

          return from(publishFilter(Group._class)).pipe(
            switchMap(publishFilter =>
              watchQuery<Sel>(
                Group._collectionName,
                {
                  $and: publishFilter ? [publishFilter, { _id: { $in: groupIDs } }] : [{ _id: { $in: groupIDs } }],
                },
                {
                  projection: gqlFields,
                },
              ),
            ),
          );
        }),
      ),
});

const getPublicSchoolInformation = registerQuery({
  type: () => [School],
  params: [
    { type: () => Boolean, name: 'getAllSchools' },
    { type: () => String, name: 'schoolID' },
  ],
  operationName: 'getPublicSchoolInformation',
  middlewares: [publicOperationMiddleware('Oeffentliche Schulliste fuer das Anmeldeformular')],
  resolve: resolveGetPublicSchoolInformation,
});

const getAllSchoolsQuery = registerQuery({
  type: () => [School],
  operationName: 'getAllSchoolsQuery',
  middlewares: [authenticatedMiddleware],
  resolve: () => (gqlFields: BaseQLSelectionSet<School>) => getAllSchools(),
});

const checkKostenstelleSuffixTakenQuery = registerQuery({
  type: () => Boolean,
  params: [
    { type: () => String, name: 'schoolID' },
    { type: () => Int, name: 'suffix' },
  ],
  operationName: 'checkKostenstelleSuffixTaken',
  middlewares: [authenticatedMiddleware],
  resolve: (schoolID: string, suffix: number) => async () => !!(await findKostenstelleWithSuffix([schoolID], suffix)),
});

const releaseGroupBillingLock = registerMutation({
  type: () => Boolean,
  operationName: 'releaseGroupBillingLock',
  middlewares: [checkPermissionMiddleware(Permission.ACCEPT_ALL_SEPA_MANDATES_FOR_BILLING_POSITION, ANY_CONTEXT)],
  params: [{ type: () => ID, name: 'groupID' }],
  resolve: resolveReleaseGroupBillingLock,
});

export const GroupsAPI = {
  subscribeGroups: subscribeGroupsPaginatedFiltered,
  subscribeSubGroups: subscribeSubGroupsPaginatedFiltered,
  subscribeForUser: subscribeGroupsOfUser,
  getPublicSchools: getPublicSchoolInformation,
  getAllSchools: getAllSchoolsQuery,
  checkKostenstelleSuffixTaken: checkKostenstelleSuffixTakenQuery,
  releaseBillingLock: releaseGroupBillingLock,
};
